/**
 * Guided Actions — Human-in-the-Loop Execution Layer (I-A-12)
 *
 * Maps each recommendation type to 1–3 predefined, explainable next-step
 * actions. Each action navigates the user into the correct context — no
 * backend mutations, no automation.
 */

export type GuidedActionType =
  | 'investigate_shop_load'
  | 'review_scheduling_mix'
  | 'inspect_completion_flow'
  | 'compare_shops_in_scope';

export interface GuidedAction {
  action_type: GuidedActionType;
  title: string;
  description: string;
  context_hint: string;
  cta_label: string;
  route_type: 'shop' | 'intelligence';
  target_shop_id?: string;
}

type RecommendationType =
  | 'OVERLOAD_ATTENTION'
  | 'UNDERUTILIZATION'
  | 'COMPLETION_WEAKNESS'
  | 'WAITER_PRESSURE'
  | 'CAPACITY_IMBALANCE';

// ── Action Templates ──────────────────────────────────

function investigateShopLoad(shopId?: string): GuidedAction {
  return {
    action_type: 'investigate_shop_load',
    title: 'Investigate Shop Load',
    description:
      'Consider reviewing current workload, waiter mix, and technician utilization at this location.',
    context_hint:
      'Look for elevated OBLI, high waiter ratios, or technicians carrying disproportionate load.',
    cta_label: 'Open Shop',
    route_type: 'shop',
    target_shop_id: shopId,
  };
}

function reviewSchedulingMix(shopId?: string): GuidedAction {
  return {
    action_type: 'review_scheduling_mix',
    title: 'Review Scheduling Mix',
    description:
      'Consider reviewing same-day intake, waiter jobs, and scheduling discipline at this shop.',
    context_hint:
      'Check whether the waiter-to-dropoff balance is appropriate and whether same-day volume is manageable.',
    cta_label: 'Open Shop',
    route_type: 'shop',
    target_shop_id: shopId,
  };
}

function inspectCompletionFlow(shopId?: string): GuidedAction {
  return {
    action_type: 'inspect_completion_flow',
    title: 'Inspect Completion Flow',
    description:
      'Consider inspecting current jobs for bottlenecks, long-running work, or completion delays.',
    context_hint:
      'Look for jobs that have been in progress longer than expected or stalled diagnostic work.',
    cta_label: 'Open Shop',
    route_type: 'shop',
    target_shop_id: shopId,
  };
}

function compareShopsInScope(): GuidedAction {
  return {
    action_type: 'compare_shops_in_scope',
    title: 'Compare Shops in Scope',
    description:
      'Consider comparing this shop against others in your scope to identify differences in workload or performance.',
    context_hint:
      'The Intelligence page shows side-by-side metrics across all shops in your scope.',
    cta_label: 'View Intelligence',
    route_type: 'intelligence',
  };
}

// ── Generation ────────────────────────────────────────

const ACTION_MAP: Record<RecommendationType, (shopId?: string) => GuidedAction[]> = {
  OVERLOAD_ATTENTION: (shopId) => [
    investigateShopLoad(shopId),
    reviewSchedulingMix(shopId),
  ],
  UNDERUTILIZATION: (_shopId) => [
    compareShopsInScope(),
  ],
  COMPLETION_WEAKNESS: (shopId) => [
    inspectCompletionFlow(shopId),
  ],
  WAITER_PRESSURE: (shopId) => [
    reviewSchedulingMix(shopId),
    investigateShopLoad(shopId),
  ],
  CAPACITY_IMBALANCE: (_shopId) => [
    compareShopsInScope(),
  ],
};

/**
 * Generate 1–3 guided actions for a recommendation.
 * Deterministic, predefined text only.
 */
export function generateGuidedActions(rec: {
  type: string;
  shop_id?: string;
}): GuidedAction[] {
  const factory = ACTION_MAP[rec.type as RecommendationType];
  if (!factory) return [];
  return factory(rec.shop_id).slice(0, 3);
}
